// React + Formik version of the Organoid Experiment Request Form
// Assumes use of Formik and optional Yup for validation

import React, { useState } from "react";
import { Formik, Field, ErrorMessage, FieldArray } from "formik";
import { Form, Spinner } from "react-bootstrap";
import RequiredAsteriskComponent from "../../components/Generic/RequiredAsteriskComponent";
import Button from "react-bootstrap/Button";
import { showToast } from "../../config_and_helpers/helpers";
import { submitOrganoidForm } from "../../clients/experiform_client";
import {
  HadassahLogo,
  HadassahResearchFundLogo,
  HocLogo,
} from "../../config_and_helpers/config";

const OrganoidExperimentForm = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = (values) => {
    setIsSubmitting(true);

    const formPromise = submitOrganoidForm(values); // no token

    formPromise.then((response) => {
      if (response && response.success) {
        setTimeout(() => {
          response.toast();
          setIsSubmitting(false);
        }, 1000);
      } else {
        showToast(
          "An unexpected error occurred. Please try again.",
          "error",
          "top-right",
          3000,
        );
        setIsSubmitting(false);
      }
    });
  };

  return (
    <div
      style={{
        maxWidth: "960px",
        margin: "0 auto",
        padding: "2rem",
        background: "#f9f9f9",
        borderRadius: "12px",
        boxShadow: "0 0 10px rgba(0,0,0,0.1)",
      }}
    >
      <div
        style={{
          position: "relative",
          height: "70px",
          marginBottom: "2rem",
        }}
      >
        {/* Left logo */}
        <img
          src={HadassahLogo}
          alt="Hadassah"
          style={{
            position: "absolute",
            left: "0",
            top: "50%",
            transform: "translateY(-50%) scale(0.8)",
            maxHeight: "100px", // or whatever upper limit you'd like
            height: "auto",
            width: "auto",
          }}
        />

        {/* Center logo */}
        <img
          src={HocLogo}
          alt="HOC"
          style={{
            position: "absolute",
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -50%) scale(0.8)",
            maxHeight: "100px", // or whatever upper limit you'd like
            height: "auto",
            width: "auto",
          }}
        />

        {/* Right logo */}
        <img
          src={HadassahResearchFundLogo}
          alt="Research Fund"
          style={{
            position: "absolute",
            right: "0",
            top: "50%",
            transform: "translateY(-50%)",
            maxHeight: "auto",
            height: "auto",
            width: "auto",
          }}
        />
      </div>

      <h2 style={{ textAlign: "center", marginBottom: "1.5rem" }}>
        Organoid Experiment Request Form
      </h2>
      <Formik
        initialValues={{
          submittedBy: "",
          contact: {
            phone: "",
            email: "",
          },
          calibration: "",
          sampleSource: [],
          organoidType: [], // changed to array
          tissueTypes: [],
          selectedTissueToAdd: "",
          otherTissueType: "",
          diseaseTypes: [],
          selectedDiseaseToAdd: "",
          mutation: "",
          cancerTypes: [], // changed to array
          selectedCancerToAdd: "",
          otherCancerType: "",
          cancerMutations: "",
          numPatients: "",
          treatmentTiming: "",
          treatments: {
            radiation: false,
            radiationDose: "",
            exposures: "",
            interval: "",
            drugTreatment: false,
            drugs: [
              {
                name: "",
                mw: "",
                mwUnit: "",
                property: "",
                stockSolvent: "",
                stockConcentration: "",
                stockConcentrationUnit: "",
                concentration: "",
                duration: "",
                customDuration: "",
              },
            ],
            bacterialInfection: false,
            bacteriaList: [
              {
                strain: "",
                concValue: "",
                concUnit: "",
                method: "",
              },
            ],
            viralInfection: false,
            virusList: [
              {
                type: "",
                size: "",
                concValue: "",
                concUnit: "",
                method: "",
              },
            ],
            enableOther: false,
            otherTreatment: "",
          },
          replicates: "",
          replicatesOther: "",
          negativeControl: "",
          negativeControlDesc: "",
          experimentDuration: "",
          experimentDurationOther: "",
          dataCollection: {
            brightfield: false,
            timepoints: [],
            otherTimepoint: "",
            timeLapse: false,
            lapseDuration: "",
            lapseInterval: "",
            fluorescence: false,
            markers: "",
            customerMarkers: "",
            enableOther: false,
            other: "",
          },
          endpoints: {
            facs: false,
            facsMarkers: "",
            facsProvided: "",
            facsLocation: "",
            ihc: false,
            ihcMarkers: "",
            ihcProvided: "",
            wholeMount: false,
            wholeMountMarkers: "",
            wholeMountTested: "",
            wholeMountProvided: "",
            viability: false,
            viabilityAssay: "",
            protein: false,
            proteinAmount: "",
            proteinVolume: "",
            proteinBuffer: "",
            dna: false,
            dnaAmount: "",
            dnaVolume: "",
            dnaBuffer: "",
            rna: false,
            rnaAmount: "",
            rnaVolume: "",
            rnaBuffer: "",
            enableOther: false,
            other: "",
          },
          generalComments: "",
        }}
        initialTouched={{
          submittedBy: true,
          contact: {
            phone: true,
            email: true,
          },
          calibration: true,
          sampleSource: true,
          organoidType: true,
          numPatients: true,
          treatmentTiming: true,
          experimentDuration: true,
          dataCollection: {
            brightfield: false,
            timepoints: false,
            timeLapse: false,
            fluorescence: false,
            enableOther: false,
          },
          endpoints: {
            facs: false,
            ihc: false,
            wholeMount: false,
            viability: false,
            protein: false,
            dna: false,
            rna: false,
            enableOther: false,
          },
        }}
        onSubmit={(values) => {
          handleSubmit(values);
        }}
      >
        {({ values, setFieldValue, handleSubmit }) => {
          return (
            // Form from react-bootstrap, using Formik's onSubmit function
            <Form noValidate onSubmit={handleSubmit}>
              <Form.Group controlId="submittedBy" className="field-margin">
                <Form.Label>
                  Submitted by <RequiredAsteriskComponent />
                </Form.Label>
                <Field name="submittedBy" className="form-control" />
                <Form.Control.Feedback type="invalid">
                  <ErrorMessage name="submittedBy" />
                </Form.Control.Feedback>
              </Form.Group>
              <Form.Group controlId="contact.phone" className="field-margin">
                <Form.Label>
                  Phone Number <RequiredAsteriskComponent />
                </Form.Label>
                <Field name="contact.phone" className="form-control" />
                <Form.Control.Feedback type="invalid">
                  <ErrorMessage name="contact.phone" />
                </Form.Control.Feedback>
              </Form.Group>
              <Form.Group controlId="contact.email" className="field-margin">
                <Form.Label>
                  Email Address <RequiredAsteriskComponent />
                </Form.Label>
                <Field
                  name="contact.email"
                  type="email"
                  className="form-control"
                />
                <Form.Control.Feedback type="invalid">
                  <ErrorMessage name="contact.email" />
                </Form.Control.Feedback>
              </Form.Group>
              <Form.Group controlId="calibration" className="field-margin">
                <Form.Label>
                  Is this a preliminary experiment?{" "}
                  <RequiredAsteriskComponent />
                </Form.Label>
                <div>
                  <label>
                    <Field type="radio" name="calibration" value="yes" /> Yes
                  </label>
                  <label style={{ marginLeft: "1rem" }}>
                    <Field type="radio" name="calibration" value="no" /> No
                  </label>
                </div>
                <Form.Control.Feedback type="invalid">
                  <ErrorMessage name="calibration" />
                </Form.Control.Feedback>
              </Form.Group>
              {/* SECTION 1: Organoid Type & Source */}
              <hr className="mt-4 mb-4 border-5 border-dark" />
              <h4 className="mb-3">Section 1: Organoid Type & Source</h4>
              <Form.Group
                controlId="sampleSource"
                className={"field-margin mt-2"}
              >
                <Form.Label>
                  Sample source: <RequiredAsteriskComponent />
                </Form.Label>
                <div>
                  {[
                    "Healthy (Solid)",
                    "Diseased (Solid)",
                    "Pleural Effusion",
                    "Ascites",
                    "Does not matter",
                  ].map((src) => (
                    <label key={src} style={{ marginRight: "1rem" }}>
                      <Field type="checkbox" name="sampleSource" value={src} />{" "}
                      {src}
                    </label>
                  ))}
                </div>
              </Form.Group>
              <hr className="my-4" style={{ borderTop: "3px solid #000" }} />
              <Form.Group
                controlId="organoidType"
                className={"field-margin mt-2"}
              >
                <Form.Label>
                  Organoid Type: <RequiredAsteriskComponent />
                </Form.Label>
                <div>
                  {/* Healthy */}
                  <div className="form-check">
                    <label className="form-check-label">
                      <input
                        type="checkbox"
                        name="organoidType"
                        value="Healthy"
                        checked={values.organoidType.includes("Healthy")}
                        onChange={(e) => {
                          const { checked, value } = e.target;
                          const current = values.organoidType;
                          if (checked) {
                            setFieldValue("organoidType", [...current, value]);
                          } else {
                            const updated = current.filter((v) => v !== value);
                            setFieldValue("organoidType", updated);
                            setFieldValue("tissueTypes", []);
                            setFieldValue("otherTissueType", "");
                          }
                        }}
                        className="form-check-input"
                      />
                      Healthy
                    </label>
                  </div>

                  {values.organoidType.includes("Healthy") && (
                    <div className="p-3 mt-3 mb-3 border rounded bg-light-subtle">
                      <p
                        className="text-muted mb-1"
                        style={{ fontSize: "0.9rem" }}
                      >
                        IMPORTANT: Click the "➕ Add" button to add your
                        selection. For multiple entries, define the AND/OR
                        relationship between them. Only applies to this section.
                      </p>

                      {values.tissueTypes.map((entry, index) => {
                        const isLast = index === values.tissueTypes.length - 1;
                        return (
                          <div
                            key={index}
                            className="d-flex align-items-center justify-content-between mb-2 border p-2 rounded bg-white"
                          >
                            <span>
                              {index + 1}.{" "}
                              {entry.tissue.charAt(0).toUpperCase() +
                                entry.tissue.slice(1)}
                            </span>
                            {!isLast && (
                              <select
                                value={entry.logic}
                                onChange={(e) => {
                                  const updated = [...values.tissueTypes];
                                  updated[index].logic = e.target.value;
                                  setFieldValue("tissueTypes", updated);
                                }}
                                className="form-select mx-2"
                                style={{ width: "100px" }}
                              >
                                <option value="AND">AND</option>
                                <option value="OR">OR</option>
                              </select>
                            )}
                            <button
                              type="button"
                              onClick={() => {
                                const updated = values.tissueTypes.filter(
                                  (_, i) => i !== index,
                                );
                                setFieldValue("tissueTypes", updated);
                              }}
                              className="btn btn-sm btn-outline-danger"
                            >
                              ✖
                            </button>
                          </div>
                        );
                      })}

                      <div className="d-flex mt-3">
                        <select
                          className="form-select"
                          id="tissueTypeDropdown"
                          value={values.selectedTissueToAdd}
                          onChange={(e) =>
                            setFieldValue("selectedTissueToAdd", e.target.value)
                          }
                        >
                          <option value="">Select tissue type</option>
                          {[
                            "nasal",
                            "esophagus",
                            "lung",
                            "breast",
                            "gastric",
                            "duodenum",
                            "colon",
                            "pancreas",
                            "endometrium",
                            "ovary",
                            "skin",
                            "Other",
                          ].map((t) => {
                            const alreadyChosen = values.tissueTypes.some(
                              (entry) => entry.tissue === t,
                            );
                            return (
                              <option
                                key={t}
                                value={t}
                                disabled={alreadyChosen}
                              >
                                {t.charAt(0).toUpperCase() + t.slice(1)}
                              </option>
                            );
                          })}
                        </select>

                        <button
                          type="button"
                          className="btn btn-outline-primary ms-2"
                          onClick={() => {
                            const selected = values.selectedTissueToAdd;
                            if (
                              selected &&
                              !values.tissueTypes.some(
                                (entry) => entry.tissue === selected,
                              )
                            ) {
                              const updated = [...values.tissueTypes];

                              // Apply logic to previous last entry if it exists
                              if (updated.length > 0) {
                                updated[updated.length - 1] = {
                                  ...updated[updated.length - 1],
                                  logic: "OR",
                                };
                              }

                              // Add new tissue without logic
                              updated.push({ tissue: selected });

                              setFieldValue("tissueTypes", updated);
                              setFieldValue("selectedTissueToAdd", "");
                            }
                          }}
                        >
                          ➕ Add
                        </button>
                      </div>

                      {values.tissueTypes.some(
                        (entry) => entry.tissue === "Other",
                      ) && (
                        <Field
                          name="otherTissueType"
                          className="form-control mt-2"
                          placeholder="Specify other tissue type..."
                        />
                      )}
                    </div>
                  )}

                  {/* Diseased */}
                  <div className="form-check mt-2">
                    <label className="form-check-label">
                      <input
                        type="checkbox"
                        name="organoidType"
                        value="Diseased"
                        checked={values.organoidType.includes("Diseased")}
                        onChange={(e) => {
                          const { checked, value } = e.target;
                          const current = values.organoidType;

                          if (checked) {
                            setFieldValue("organoidType", [...current, value]);
                            if (values.diseaseTypes.length === 0) {
                              setFieldValue("diseaseTypes", [
                                { disease: "", mutation: "" },
                              ]);
                            }
                          } else {
                            const updated = current.filter((v) => v !== value);
                            setFieldValue("organoidType", updated);
                            setFieldValue("diseaseTypes", []);
                          }
                        }}
                        className="form-check-input"
                      />
                      Diseased (non-cancer)
                    </label>
                  </div>

                  {values.organoidType.includes("Diseased") && (
                    <div className="p-3 mt-3 mb-3 border rounded bg-light-subtle">
                      {values.diseaseTypes.map((entry, index) => (
                        <div
                          key={index}
                          className="mb-3 p-3 border rounded bg-white"
                        >
                          <Field
                            name={`diseaseTypes[${index}].disease`}
                            className="form-control mb-2"
                            placeholder="Disease type"
                          />
                          <Field
                            name={`diseaseTypes[${index}].mutation`}
                            className="form-control"
                            placeholder="Target mutation (if any)"
                          />
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger mt-2"
                            onClick={() => {
                              const updated = [...values.diseaseTypes];
                              updated.splice(index, 1);
                              setFieldValue("diseaseTypes", updated);
                            }}
                          >
                            ✖ Remove
                          </button>
                        </div>
                      ))}
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary"
                        onClick={() =>
                          setFieldValue("diseaseTypes", [
                            ...values.diseaseTypes,
                            { disease: "", mutation: "" },
                          ])
                        }
                      >
                        ➕ Add Disease
                      </button>
                    </div>
                  )}

                  {/* Cancer */}
                  <div className="form-check mt-2">
                    <label className="form-check-label">
                      <input
                        type="checkbox"
                        name="organoidType"
                        value="Cancer"
                        checked={values.organoidType.includes("Cancer")}
                        onChange={(e) => {
                          const { checked, value } = e.target;
                          const current = values.organoidType;
                          if (checked) {
                            setFieldValue("organoidType", [...current, value]);
                          } else {
                            const updated = current.filter((v) => v !== value);
                            setFieldValue("organoidType", updated);
                            setFieldValue("cancerTypes", []);
                            setFieldValue("otherCancerType", "");
                            setFieldValue("cancerMutations", "");
                          }
                        }}
                        className="form-check-input"
                      />
                      Cancer
                    </label>
                  </div>

                  {values.organoidType.includes("Cancer") && (
                    <div className="p-3 mt-3 mb-3 border rounded bg-light-subtle">
                      <p
                        className="text-muted mb-1"
                        style={{ fontSize: "0.9rem" }}
                      >
                        IMPORTANT: Click the "➕ Add" button to add your
                        selection. For multiple entries, define the AND/OR
                        relationship between them. Only applies to this section.
                      </p>

                      {values.cancerTypes.map((entry, index) => {
                        const isLast = index === values.cancerTypes.length - 1;
                        return (
                          <div
                            key={index}
                            className="d-flex align-items-center justify-content-between mb-2 border p-2 rounded bg-white"
                          >
                            <span>
                              {index + 1}.{" "}
                              {entry.tissue.charAt(0).toUpperCase() +
                                entry.tissue.slice(1)}
                            </span>
                            {!isLast && (
                              <select
                                value={entry.logic}
                                onChange={(e) => {
                                  const updated = [...values.cancerTypes];
                                  updated[index].logic = e.target.value;
                                  setFieldValue("cancerTypes", updated);
                                }}
                                className="form-select mx-2"
                                style={{ width: "100px" }}
                              >
                                <option value="AND">AND</option>
                                <option value="OR">OR</option>
                              </select>
                            )}
                            <button
                              type="button"
                              onClick={() => {
                                const updated = values.cancerTypes.filter(
                                  (_, i) => i !== index,
                                );
                                setFieldValue("cancerTypes", updated);
                              }}
                              className="btn btn-sm btn-outline-danger"
                            >
                              ✖
                            </button>
                          </div>
                        );
                      })}

                      <div className="d-flex mt-3">
                        <select
                          className="form-select"
                          value={values.selectedCancerToAdd}
                          onChange={(e) =>
                            setFieldValue("selectedCancerToAdd", e.target.value)
                          }
                        >
                          <option value="">Select cancer type</option>
                          {[
                            "esophagus",
                            "lung",
                            "breast",
                            "gastric",
                            "duodenum",
                            "colon",
                            "pancreas",
                            "endometrium",
                            "ovary",
                            "Other",
                          ].map((t) => {
                            const isDisabled = values.cancerTypes.some(
                              (entry) => entry.tissue === t,
                            );
                            return (
                              <option key={t} value={t} disabled={isDisabled}>
                                {t.charAt(0).toUpperCase() + t.slice(1)}
                              </option>
                            );
                          })}
                        </select>

                        <button
                          type="button"
                          className="btn btn-outline-primary ms-2"
                          onClick={() => {
                            const selected = values.selectedCancerToAdd;
                            if (
                              selected &&
                              !values.cancerTypes.some(
                                (entry) => entry.tissue === selected,
                              )
                            ) {
                              const updated = [...values.cancerTypes];

                              // Add logic to previous last entry if needed
                              if (updated.length > 0) {
                                updated[updated.length - 1] = {
                                  ...updated[updated.length - 1],
                                  logic: "OR",
                                };
                              }

                              // Add new entry without logic
                              updated.push({ tissue: selected });

                              setFieldValue("cancerTypes", updated);
                              setFieldValue("selectedCancerToAdd", "");
                            }
                          }}
                        >
                          ➕ Add
                        </button>
                      </div>

                      {values.cancerTypes.some(
                        (entry) => entry.tissue === "Other",
                      ) && (
                        <Field
                          name="otherCancerType"
                          className="form-control mt-2"
                          placeholder="Specify other cancer type..."
                        />
                      )}

                      <Field
                        name="cancerMutations"
                        className="form-control mt-3"
                        placeholder="Target mutations (if any)"
                      />
                    </div>
                  )}
                </div>
              </Form.Group>
              <hr className="my-4" />
              <Form.Group controlId="numPatients" className="field-margin">
                <Form.Label>
                  Number of patients: <RequiredAsteriskComponent />
                </Form.Label>
                <Field name="numPatients" className="form-control" />
              </Form.Group>
              <hr className="my-4" />
              <Form.Group controlId="treatmentTiming" className="field-margin">
                <Form.Label>
                  Timing of sample collection <RequiredAsteriskComponent />
                </Form.Label>
                <div>
                  <label>
                    <Field
                      type="radio"
                      name="treatmentTiming"
                      value="Before treatment"
                    />{" "}
                    Before treatment
                  </label>
                  <label style={{ marginLeft: "1rem" }}>
                    <Field
                      type="radio"
                      name="treatmentTiming"
                      value="After treatment"
                    />{" "}
                    After treatment
                  </label>
                  <label style={{ marginLeft: "1rem" }}>
                    <Field
                      type="radio"
                      name="treatmentTiming"
                      value="Does not matter"
                    />{" "}
                    Does not matter
                  </label>
                </div>
              </Form.Group>
              {/* Divider and Header */}
              <hr className="mt-4 mb-4 border-5 border-dark" />
              <h4 className="mb-3">Section 2: Experimental Treatment</h4>
              <Form.Label>
                Type of treatment: <RequiredAsteriskComponent />
              </Form.Label>
              <Form.Group
                controlId="treatments.radiation"
                className={`field-margin mt-2 ${
                  values.treatments.radiation
                    ? "p-3 mt-3 mb-3 border rounded bg-light-subtle"
                    : ""
                }`}
              >
                <div className="form-check">
                  <input
                    type="checkbox"
                    name="treatments.radiation"
                    checked={values.treatments.radiation}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setFieldValue("treatments.radiation", checked);
                      if (!checked) {
                        setFieldValue("treatments.radiationDose", "");
                        setFieldValue("treatments.exposures", "");
                        setFieldValue("treatments.interval", "");
                      }
                    }}
                    className="form-check-input"
                  />
                  <Form.Label className="form-check-label">
                    Radiation
                  </Form.Label>
                </div>
                {values.treatments.radiation && (
                  <div className="p-3 mt-3 mb-3 border rounded bg-light-subtle">
                    <Field
                      name="treatments.radiationDose"
                      className="form-control mb-2"
                      placeholder="Dose (Gy)"
                    />
                    <Field
                      name="treatments.exposures"
                      className="form-control mb-2"
                      placeholder="Number of exposures"
                    />
                    <Field
                      name="treatments.interval"
                      className="form-control"
                      placeholder="Exposure interval (days)"
                    />
                  </div>
                )}
              </Form.Group>
              <Form.Group
                controlId="treatments.drugTreatment"
                className={`field-margin mt-2 ${
                  values.treatments.drugTreatment
                    ? "p-3 mt-3 mb-3 border rounded bg-light-subtle"
                    : ""
                }`}
              >
                <div className="form-check">
                  <input
                    type="checkbox"
                    name="treatments.drugTreatment"
                    checked={values.treatments.drugTreatment}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setFieldValue("treatments.drugTreatment", checked);
                      if (!checked) {
                        setFieldValue("treatments.drugs", [
                          {
                            name: "",
                            mw: "",
                            mwUnit: "",
                            property: "",
                            stockSolvent: "",
                            stockConcentration: "",
                            stockConcentrationUnit: "",
                            concentration: "",
                            duration: "",
                            customDuration: "",
                          },
                        ]);
                      }
                    }}
                    className="form-check-input"
                  />
                  <Form.Label className="form-check-label">
                    Drug Treatment
                  </Form.Label>
                </div>
                {values.treatments.drugTreatment && (
                  <div className="p-3 mt-3 mb-3 border rounded bg-light-subtle">
                    <FieldArray name="treatments.drugs">
                      {({ push, remove }) => (
                        <>
                          {values.treatments.drugs.map((drug, index) => (
                            <div
                              key={index}
                              className="mb-4 p-3 border rounded bg-white"
                            >
                              <Field
                                name={`treatments.drugs[${index}].name`}
                                className="form-control mb-2"
                                placeholder="Molecule name"
                              />
                              <div className="mb-2 d-flex">
                                <Field
                                  name={`treatments.drugs[${index}].mw`}
                                  className="form-control me-2"
                                  placeholder="Molecular weight"
                                  style={{ maxWidth: "70%" }}
                                />
                                <Field
                                  as="select"
                                  name={`treatments.drugs[${index}].mwUnit`}
                                  className="form-select"
                                  style={{ maxWidth: "30%" }}
                                >
                                  <option disabled value="">
                                    -- Select Unit --
                                  </option>
                                  <option value="g/mol">g/mol</option>
                                  <option value="Da">Da</option>
                                  <option value="kDa">kDa</option>
                                </Field>
                              </div>
                              <div className="mb-2">
                                <label className="me-3">
                                  <Field
                                    type="radio"
                                    name={`treatments.drugs[${index}].property`}
                                    value="Hydrophobic"
                                  />{" "}
                                  Hydrophobic
                                </label>
                                <label className="me-3">
                                  <Field
                                    type="radio"
                                    name={`treatments.drugs[${index}].property`}
                                    value="Hydrophilic"
                                  />{" "}
                                  Hydrophilic
                                </label>
                                <label>
                                  <Field
                                    type="radio"
                                    name={`treatments.drugs[${index}].property`}
                                    value="Unknown"
                                  />{" "}
                                  Unknown
                                </label>
                              </div>
                              <div className="mb-2">
                                <div className="mb-2">
                                  <Field
                                    name={`treatments.drugs[${index}].stockSolvent`}
                                    className="form-control"
                                    placeholder="Stock solvent (e.g. DMSO, PBS)"
                                  />
                                </div>
                                <div className="d-flex">
                                  <Field
                                    name={`treatments.drugs[${index}].stockConcentration`}
                                    className="form-control me-2"
                                    placeholder="Stock concentration"
                                    style={{ maxWidth: "70%" }}
                                  />
                                  <Field
                                    as="select"
                                    name={`treatments.drugs[${index}].stockConcentrationUnit`}
                                    className="form-select"
                                    style={{ maxWidth: "30%" }}
                                  >
                                    <option disabled value="">
                                      -- Select Unit --
                                    </option>
                                    <option value="mM">mM</option>
                                    <option value="μM">μM</option>
                                    <option value="mg/mL">mg/mL</option>
                                    <option value="%">%</option>
                                  </Field>
                                </div>
                              </div>

                              <Field
                                name={`treatments.drugs[${index}].concentration`}
                                className="form-control mb-2"
                                placeholder="Treatment concentration(s)"
                              />
                              <div className="mb-2 d-flex align-items-center">
                                <label className="me-3">
                                  <Field
                                    type="radio"
                                    name={`treatments.drugs[${index}].duration`}
                                    value="3 days"
                                  />{" "}
                                  3 days
                                </label>
                                <label className="me-3">
                                  <Field
                                    type="radio"
                                    name={`treatments.drugs[${index}].duration`}
                                    value="6 days"
                                  />{" "}
                                  6 days
                                </label>
                                <label className="me-2">
                                  <Field
                                    type="radio"
                                    name={`treatments.drugs[${index}].duration`}
                                    value="Other"
                                  />{" "}
                                  Other
                                </label>
                                {drug.duration === "Other" && (
                                  <Field
                                    name={`treatments.drugs[${index}].customDuration`}
                                    className="form-control ms-2"
                                    placeholder="Specify duration"
                                    style={{ width: "500px" }}
                                  />
                                )}
                              </div>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => remove(index)}
                              >
                                Remove Molecule
                              </button>
                            </div>
                          ))}
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            onClick={() =>
                              push({
                                name: "",
                                mw: "",
                                mwUnit: "",
                                property: "",
                                stockSolvent: "",
                                stockConcentration: "",
                                stockConcentrationUnit: "",
                                concentration: "",
                                duration: "",
                                customDuration: "",
                              })
                            }
                          >
                            Add Molecule
                          </button>
                        </>
                      )}
                    </FieldArray>
                  </div>
                )}
              </Form.Group>
              <Form.Group
                controlId="treatments.bacterialInfection"
                className={`field-margin mt-2 ${
                  values.treatments.bacterialInfection
                    ? "p-3 mt-3 mb-3 border rounded bg-light-subtle"
                    : ""
                }`}
              >
                <div className="form-check">
                  <input
                    type="checkbox"
                    name="treatments.bacterialInfection"
                    checked={values.treatments.bacterialInfection}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setFieldValue("treatments.bacterialInfection", checked);
                      if (!checked) {
                        setFieldValue("treatments.bacteriaList", []);
                      } else {
                        setFieldValue("treatments.bacteriaList", [
                          {
                            strain: "",
                            concValue: "",
                            concUnit: "",
                            method: "",
                          },
                        ]);
                      }
                    }}
                    className="form-check-input"
                  />
                  <Form.Label className="form-check-label">
                    Bacterial Infection
                  </Form.Label>
                </div>

                {values.treatments.bacterialInfection && (
                  <FieldArray name="treatments.bacteriaList">
                    {({ push, remove }) => (
                      <>
                        {values.treatments.bacteriaList.map((b, index) => (
                          <div
                            key={index}
                            className="mb-3 p-3 border rounded bg-light-subtle"
                          >
                            <Field
                              name={`treatments.bacteriaList[${index}].strain`}
                              className="form-control mb-2"
                              placeholder="Bacterial strain/type"
                            />
                            <div className="d-flex mb-2">
                              <Field
                                name={`treatments.bacteriaList[${index}].concValue`}
                                className="form-control me-2"
                                placeholder="Concentration"
                              />
                              <Field
                                as="select"
                                name={`treatments.bacteriaList[${index}].concUnit`}
                                className="form-select"
                              >
                                <option disabled value="">
                                  -- Select Unit --
                                </option>
                                <option value="CFU/mL">CFU/mL</option>
                                <option value="OD600">OD600</option>
                                <option value="MOI">MOI</option>
                              </Field>
                            </div>
                            <Field
                              name={`treatments.bacteriaList[${index}].method`}
                              className="form-control mb-2"
                              placeholder="Infection method on 2D samples"
                            />
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => remove(index)}
                            >
                              Remove Bacterium
                            </button>
                          </div>
                        ))}
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary"
                          onClick={() =>
                            push({
                              strain: "",
                              concValue: "",
                              concUnit: "",
                              method: "",
                            })
                          }
                        >
                          Add Bacterium
                        </button>
                      </>
                    )}
                  </FieldArray>
                )}
              </Form.Group>

              <Form.Group
                controlId="treatments.viralInfection"
                className={`field-margin mt-2 ${
                  values.treatments.viralInfection
                    ? "p-3 mt-3 mb-3 border rounded bg-light-subtle"
                    : ""
                }`}
              >
                <div className="form-check">
                  <input
                    type="checkbox"
                    name="treatments.viralInfection"
                    checked={values.treatments.viralInfection}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setFieldValue("treatments.viralInfection", checked);
                      if (!checked) {
                        setFieldValue("treatments.virusList", [
                          {
                            type: "",
                            size: "",
                            concValue: "",
                            concUnit: "",
                            method: "",
                          },
                        ]);
                      }
                    }}
                    className="form-check-input"
                  />
                  <Form.Label className="form-check-label">
                    Viral Infection
                  </Form.Label>
                </div>

                {values.treatments.viralInfection && (
                  <FieldArray name="treatments.virusList">
                    {({ push, remove }) => (
                      <>
                        {values.treatments.virusList.map((virus, index) => (
                          <div
                            key={index}
                            className="p-3 mt-3 mb-3 border rounded bg-light-subtle"
                          >
                            <Field
                              name={`treatments.virusList[${index}].type`}
                              className="form-control mb-2"
                              placeholder="Virus type"
                            />
                            <Field
                              name={`treatments.virusList[${index}].size`}
                              className="form-control mb-2"
                              placeholder="Approximate particle size (nm)"
                            />
                            <div className="d-flex mb-2">
                              <Field
                                name={`treatments.virusList[${index}].concValue`}
                                className="form-control me-2"
                                placeholder="Concentration"
                              />
                              <Field
                                as="select"
                                name={`treatments.virusList[${index}].concUnit`}
                                className="form-select"
                              >
                                <option disabled value="">
                                  -- Select Unit --
                                </option>
                                <option value="MOI">MOI</option>
                                <option value="PFU/mL">PFU/mL</option>
                                <option value="copies/mL">copies/mL</option>
                              </Field>
                            </div>
                            <Field
                              name={`treatments.virusList[${index}].method`}
                              className="form-control mb-2"
                              placeholder="Infection method on 2D samples"
                            />
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => remove(index)}
                            >
                              Remove Virus
                            </button>
                          </div>
                        ))}
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary"
                          onClick={() =>
                            push({
                              type: "",
                              size: "",
                              concValue: "",
                              concUnit: "",
                              method: "",
                            })
                          }
                        >
                          Add Virus
                        </button>
                      </>
                    )}
                  </FieldArray>
                )}
              </Form.Group>

              <Form.Group
                controlId="treatments.otherTreatment"
                className={`field-margin mt-2 ${
                  values.treatments.enableOther
                    ? "p-3 mt-3 mb-3 border rounded bg-light-subtle"
                    : ""
                }`}
              >
                <div className="form-check">
                  <input
                    type="checkbox"
                    name="treatments.enableOther"
                    checked={values.treatments.enableOther}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setFieldValue("treatments.enableOther", checked);
                      if (!checked) {
                        setFieldValue("treatments.otherTreatment", "");
                      }
                    }}
                    className="form-check-input"
                  />
                  <Form.Label className="form-check-label">
                    Other Treatment
                  </Form.Label>
                </div>
                {values.treatments.enableOther && (
                  <div className="p-3 mt-3 mb-3">
                    <Field
                      as="textarea"
                      name="treatments.otherTreatment"
                      className="form-control"
                      placeholder="Please describe"
                      rows={3}
                    />
                  </div>
                )}
              </Form.Group>
              <hr className="mt-4 mb-4 border-5 border-dark" />
              <h4 className="mb-3">Section 3: Replicates & Controls</h4>
              <Form.Group controlId="replicates" className="field-margin">
                <Form.Label>
                  Replicates per condition: <RequiredAsteriskComponent />
                </Form.Label>
                <div className="d-flex align-items-center">
                  <label className="me-3">
                    <input
                      type="radio"
                      name="replicates"
                      value="Duplicate"
                      checked={values.replicates === "Duplicate"}
                      onChange={(e) => {
                        setFieldValue("replicates", e.target.value);
                        setFieldValue("replicatesOther", "");
                      }}
                    />{" "}
                    Duplicate
                  </label>
                  <label className="me-3">
                    <input
                      type="radio"
                      name="replicates"
                      value="Triplicate"
                      checked={values.replicates === "Triplicate"}
                      onChange={(e) => {
                        setFieldValue("replicates", e.target.value);
                        setFieldValue("replicatesOther", "");
                      }}
                    />{" "}
                    Triplicate
                  </label>
                  <label className="me-2">
                    <input
                      type="radio"
                      name="replicates"
                      value="Other"
                      checked={values.replicates === "Other"}
                      onChange={(e) => {
                        setFieldValue("replicates", e.target.value);
                      }}
                    />{" "}
                    Other
                  </label>

                  {values.replicates === "Other" && (
                    <Field
                      name="replicatesOther"
                      className="form-control ms-2"
                      placeholder="Specify"
                      style={{ width: "500px" }}
                    />
                  )}
                </div>
              </Form.Group>
              <Form.Group controlId="negativeControl" className="field-margin">
                <Form.Label>
                  Negative control needed? <RequiredAsteriskComponent />
                </Form.Label>
                <div>
                  <label>
                    <Field type="radio" name="negativeControl" value="Yes" />{" "}
                    Yes
                  </label>
                  <label style={{ marginLeft: "1rem" }}>
                    <Field type="radio" name="negativeControl" value="No" /> No
                  </label>
                </div>
                {values.negativeControl === "Yes" && (
                  <Field
                    name="negativeControlDesc"
                    className="form-control mt-2"
                    placeholder="If yes, describe"
                  />
                )}
              </Form.Group>
              {/* Divider and Header */}
              <hr className="mt-4 mb-4 border-5 border-dark" />
              <h4 className="mb-3">Section 4: Experiment Duration</h4>
              <Form.Group
                controlId="experimentDuration"
                className="field-margin"
              >
                <Form.Label>
                  Planned total duration: <RequiredAsteriskComponent />
                </Form.Label>
                <div className="d-flex align-items-center">
                  {["24 hours", "3 days", "7 days", "2 weeks"].map((val) => (
                    <label className="me-3" key={val}>
                      <input
                        type="radio"
                        name="experimentDuration"
                        value={val}
                        checked={values.experimentDuration === val}
                        onChange={(e) => {
                          setFieldValue("experimentDuration", e.target.value);
                          setFieldValue("experimentDurationOther", "");
                        }}
                      />{" "}
                      {val}
                    </label>
                  ))}

                  <label className="me-2">
                    <input
                      type="radio"
                      name="experimentDuration"
                      value="Other"
                      checked={values.experimentDuration === "Other"}
                      onChange={(e) => {
                        setFieldValue("experimentDuration", e.target.value);
                      }}
                    />{" "}
                    Other
                  </label>

                  {values.experimentDuration === "Other" && (
                    <Field
                      name="experimentDurationOther"
                      className="form-control ms-2"
                      placeholder="Specify"
                      style={{ width: "500px" }}
                    />
                  )}
                </div>
              </Form.Group>
              {/* Divider and Header */}
              <hr className="mt-4 mb-4 border-5 border-dark" />
              <h4 className="mb-3">
                Section 5: Data Collection During the Experiment
              </h4>
              <label>
                Select data collection type: <RequiredAsteriskComponent />
              </label>
              <Form.Group
                controlId="dataCollection.brightfield"
                className={`field-margin mt-2 ${
                  values.dataCollection.brightfield
                    ? "p-3 mt-3 mb-3 border rounded bg-light-subtle"
                    : ""
                }`}
              >
                <div className="form-check">
                  <input
                    type="checkbox"
                    name="dataCollection.brightfield"
                    checked={values.dataCollection.brightfield}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setFieldValue("dataCollection.brightfield", checked);
                      if (!checked) {
                        setFieldValue("dataCollection.timepoints", []);
                        setFieldValue("dataCollection.otherTimepoint", "");
                      }
                    }}
                    className="form-check-input"
                  />
                  <Form.Label className="form-check-label">
                    Brightfield Imaging at Specific Timepoints
                  </Form.Label>
                </div>
                {values.dataCollection.brightfield && (
                  <div className="p-3 mt-3 mb-3 border rounded bg-light-subtle">
                    <label className="me-3">
                      <Field
                        type="checkbox"
                        name="dataCollection.timepoints"
                        value="Start"
                      />{" "}
                      Start
                    </label>
                    <label className="me-3">
                      <Field
                        type="checkbox"
                        name="dataCollection.timepoints"
                        value="End"
                      />{" "}
                      End
                    </label>
                    <label className="me-2">
                      <Field
                        type="checkbox"
                        name="dataCollection.timepoints"
                        value="Other"
                      />{" "}
                      Other
                    </label>
                    {values.dataCollection.timepoints?.includes("Other") && (
                      <Field
                        name="dataCollection.otherTimepoint"
                        className="form-control mt-2"
                        placeholder="Specify other timepoint(s)"
                      />
                    )}
                  </div>
                )}
              </Form.Group>
              <Form.Group
                controlId="dataCollection.timeLapse"
                className={`field-margin mt-2 ${
                  values.dataCollection.timeLapse
                    ? "p-3 mt-3 mb-3 border rounded bg-light-subtle"
                    : ""
                }`}
              >
                <div className="form-check">
                  <input
                    type="checkbox"
                    name="dataCollection.timeLapse"
                    checked={values.dataCollection.timeLapse}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setFieldValue("dataCollection.timeLapse", checked);
                      if (!checked) {
                        setFieldValue("dataCollection.lapseDuration", "");
                        setFieldValue("dataCollection.lapseInterval", "");
                      }
                    }}
                    className="form-check-input"
                  />
                  <Form.Label className="form-check-label">
                    Time-Lapse Imaging (Longitudinal Monitoring)
                  </Form.Label>
                </div>
                {values.dataCollection.timeLapse && (
                  <div className="p-3 mt-3 mb-3 border rounded bg-light-subtle">
                    <Field
                      name="dataCollection.lapseDuration"
                      className="form-control mb-2"
                      placeholder="Total duration (days)"
                    />
                    <Field
                      name="dataCollection.lapseInterval"
                      className="form-control"
                      placeholder="Interval between images (hours)"
                    />
                  </div>
                )}
              </Form.Group>
              <Form.Group
                controlId="dataCollection.fluorescence"
                className={`field-margin mt-2 ${
                  values.dataCollection.fluorescence
                    ? "p-3 mt-3 mb-3 border rounded bg-light-subtle"
                    : ""
                }`}
              >
                <div className="form-check">
                  <input
                    type="checkbox"
                    name="dataCollection.fluorescence"
                    checked={values.dataCollection.fluorescence}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setFieldValue("dataCollection.fluorescence", checked);
                      if (!checked) {
                        setFieldValue("dataCollection.markers", "");
                        setFieldValue("dataCollection.customerMarkers", "");
                      }
                    }}
                    className="form-check-input"
                  />
                  <Form.Label className="form-check-label">
                    Fluorescence Microscopy
                  </Form.Label>
                </div>
                {values.dataCollection.fluorescence && (
                  <div className="p-3 mt-3 mb-3 border rounded bg-light-subtle">
                    <Field
                      name="dataCollection.markers"
                      className="form-control mb-2"
                      placeholder="Markers or stains (if known)"
                    />
                    <div className="d-flex align-items-center">
                      <Form.Label className="me-3 mb-0">
                        Markers provided by customer:
                      </Form.Label>
                      <div className="form-check me-3">
                        <label className="form-check-label">
                          <Field
                            type="radio"
                            name="dataCollection.customerMarkers"
                            value="Yes"
                            className="form-check-input me-1"
                          />
                          Yes
                        </label>
                      </div>
                      <div className="form-check">
                        <label className="form-check-label">
                          <Field
                            type="radio"
                            name="dataCollection.customerMarkers"
                            value="No"
                            className="form-check-input me-1"
                          />
                          No
                        </label>
                      </div>
                    </div>
                  </div>
                )}
              </Form.Group>
              <Form.Group
                controlId="dataCollection.other"
                className={`field-margin mt-2 ${
                  values.dataCollection.enableOther
                    ? "p-3 mt-3 mb-3 border rounded bg-light-subtle"
                    : ""
                }`}
              >
                <div className="form-check">
                  <input
                    type="checkbox"
                    name="dataCollection.enableOther"
                    checked={values.dataCollection.enableOther}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setFieldValue("dataCollection.enableOther", checked);
                      if (!checked) {
                        setFieldValue("dataCollection.other", "");
                      }
                    }}
                    className="form-check-input"
                  />
                  <Form.Label className="form-check-label">
                    Other Data Types
                  </Form.Label>
                </div>

                {values.dataCollection.enableOther && (
                  <div className="p-3 mt-3 mb-3">
                    <Field
                      as="textarea"
                      name="dataCollection.other"
                      className="form-control"
                      placeholder="Please specify"
                      rows={3}
                    />
                  </div>
                )}
              </Form.Group>

              {/* Divider and Header */}
              <hr className="mt-4 mb-4 border-5 border-dark" />
              <h4 className="mb-3">Section 6: End Point Analyses</h4>
              <label>
                Select end point analysis: <RequiredAsteriskComponent />
              </label>
              <Form.Group
                controlId="endpoints.facs"
                className={`field-margin mt-2 ${
                  values.endpoints.facs
                    ? "p-3 mt-3 mb-3 border rounded bg-light-subtle"
                    : ""
                }`}
              >
                <div className="form-check ">
                  <input
                    type="checkbox"
                    name="endpoints.facs"
                    checked={values.endpoints.facs}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setFieldValue("endpoints.facs", checked);
                      if (!checked) {
                        setFieldValue("endpoints.facsMarkers", "");
                        setFieldValue("endpoints.facsProvided", "");
                        setFieldValue("endpoints.facsLocation", "");
                      }
                    }}
                    className="form-check-input"
                  />

                  <Form.Label className="form-check-label">
                    FACS (Flow Cytometry)
                  </Form.Label>
                </div>
                {values.endpoints.facs && (
                  <>
                    <Field
                      name="endpoints.facsMarkers"
                      className="form-control mt-2"
                      placeholder="Markers"
                    />
                    <div className="p-3 mt-3 mb-3 border rounded bg-light-subtle">
                      <label className="me-3">
                        Are antibodies provided by the customer?
                        <Field
                          type="radio"
                          name="endpoints.facsProvided"
                          value="Yes"
                          className="ms-3"
                        />{" "}
                        Yes
                      </label>
                      <label>
                        <Field
                          type="radio"
                          name="endpoints.facsProvided"
                          value="No"
                        />{" "}
                        No
                      </label>
                    </div>

                    <div className="p-3 mt-3 mb-3 border rounded bg-light-subtle">
                      <label className="me-3">
                        Will the FACS analysis be performed at our lab?
                        <Field
                          type="radio"
                          name="endpoints.facsLocation"
                          value="Yes"
                          className="ms-3"
                        />{" "}
                        Yes
                      </label>
                      <label>
                        <Field
                          type="radio"
                          name="endpoints.facsLocation"
                          value="No"
                        />{" "}
                        No
                      </label>
                    </div>
                  </>
                )}
              </Form.Group>
              <Form.Group
                controlId="endpoints.ihc"
                className={`field-margin mt-2 ${
                  values.endpoints.ihc
                    ? "p-3 mt-3 mb-3 border rounded bg-light-subtle"
                    : ""
                }`}
              >
                <div className="form-check">
                  <input
                    type="checkbox"
                    name="endpoints.ihc"
                    checked={values.endpoints.ihc}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setFieldValue("endpoints.ihc", checked);
                      if (!checked) {
                        setFieldValue("endpoints.ihcMarkers", "");
                        setFieldValue("endpoints.ihcProvided", "");
                      }
                    }}
                    className="form-check-input"
                  />

                  <Form.Label className="form-check-label">
                    IHC on Paraffin Sections
                  </Form.Label>
                </div>
                {values.endpoints.ihc && (
                  <>
                    <Field
                      name="endpoints.ihcMarkers"
                      className="form-control mt-2"
                      placeholder="Markers to be stained"
                    />
                    <div className="p-3 mt-3 mb-3 border rounded bg-light-subtle">
                      <label className="me-3">
                        Are antibodies provided by the customer?
                        <Field
                          type="radio"
                          name="endpoints.ihcProvided"
                          value="Yes"
                          className="ms-3"
                        />{" "}
                        Yes
                      </label>
                      <label>
                        <Field
                          type="radio"
                          name="endpoints.ihcProvided"
                          value="No"
                        />{" "}
                        No
                      </label>
                    </div>
                  </>
                )}
              </Form.Group>
              <Form.Group
                controlId="endpoints.wholeMount"
                className={`field-margin mt-2 ${
                  values.endpoints.wholeMount
                    ? "p-3 mt-3 mb-3 border rounded bg-light-subtle"
                    : ""
                }`}
              >
                <div className="form-check">
                  <input
                    type="checkbox"
                    name="endpoints.wholeMount"
                    checked={values.endpoints.wholeMount}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setFieldValue("endpoints.wholeMount", checked);
                      if (!checked) {
                        setFieldValue("endpoints.wholeMountMarkers", "");
                        setFieldValue("endpoints.wholeMountTested", "");
                        setFieldValue("endpoints.wholeMountProvided", "");
                      }
                    }}
                    className="form-check-input"
                  />

                  <Form.Label className="form-check-label">
                    Whole-Mount Immunostaining of Organoids
                  </Form.Label>
                </div>
                {values.endpoints.wholeMount && (
                  <>
                    <Field
                      name="endpoints.wholeMountMarkers"
                      className="form-control mt-2"
                      placeholder="Markers to be stained"
                    />
                    <div className="p-3 mt-3 mb-3 border rounded bg-light-subtle">
                      <label className="me-2">
                        Have these antibodies been tested and shown to work on
                        whole-mount organoids?
                        <Field
                          type="radio"
                          name="endpoints.wholeMountTested"
                          value="Yes"
                          className="ms-3"
                        />{" "}
                        Yes
                      </label>
                      <label className="me-3">
                        <Field
                          type="radio"
                          name="endpoints.wholeMountTested"
                          value="No"
                          className="ms-1"
                        />{" "}
                        No
                      </label>
                      <label>
                        <Field
                          type="radio"
                          name="endpoints.wholeMountTested"
                          value="Unknown"
                        />{" "}
                        Unknown
                      </label>
                    </div>
                    <div className="mt-2">
                      <label className="me-3">
                        Are antibodies provided by the customer?
                        <Field
                          type="radio"
                          name="endpoints.wholeMountProvided"
                          value="Yes"
                          className="ms-3"
                        />{" "}
                        Yes
                      </label>
                      <label>
                        <Field
                          type="radio"
                          name="endpoints.wholeMountProvided"
                          value="No"
                        />{" "}
                        No
                      </label>
                    </div>
                  </>
                )}
              </Form.Group>
              <Form.Group
                controlId="endpoints.viability"
                className={`field-margin mt-2 ${
                  values.endpoints.viability
                    ? "p-3 mt-3 mb-3 border rounded bg-light-subtle"
                    : ""
                }`}
              >
                <div className="form-check">
                  <input
                    type="checkbox"
                    name="endpoints.viability"
                    checked={values.endpoints.viability}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setFieldValue("endpoints.viability", checked);
                      if (!checked) {
                        setFieldValue("endpoints.viabilityAssay", "");
                      }
                    }}
                    className="form-check-input"
                  />

                  <Form.Label className="form-check-label">
                    Viability Assays (e.g., XTT, CellTiter-Glo)
                  </Form.Label>
                </div>

                {values.endpoints.viability && (
                  <Field
                    name="endpoints.viabilityAssay"
                    className="p-3 mt-3 mb-3 border rounded bg-light-subtle"
                    placeholder="Specify assay"
                  />
                )}
              </Form.Group>
              <Form.Group
                controlId="endpoints.protein"
                className={`field-margin mt-2 ${
                  values.endpoints.protein
                    ? "p-3 mt-3 mb-3 border rounded bg-light-subtle"
                    : ""
                }`}
              >
                <div className="form-check">
                  <input
                    type="checkbox"
                    name="endpoints.protein"
                    checked={values.endpoints.protein}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setFieldValue("endpoints.protein", checked);
                      if (!checked) {
                        setFieldValue("endpoints.proteinAmount", "");
                        setFieldValue("endpoints.proteinVolume", "");
                        setFieldValue("endpoints.proteinBuffer", "");
                      }
                    }}
                    className="form-check-input"
                  />

                  <Form.Label className="form-check-label">
                    Protein Extraction
                  </Form.Label>
                </div>
                {values.endpoints.protein && (
                  <div className="p-3 mt-3 mb-3 border rounded bg-light-subtle">
                    <Field
                      name="endpoints.proteinAmount"
                      className="form-control mt-2"
                      placeholder="Desired amount (µg)"
                    />
                    <Field
                      name="endpoints.proteinVolume"
                      className="form-control mt-2"
                      placeholder="Final volume (µL)"
                    />
                    <Field
                      name="endpoints.proteinBuffer"
                      className="form-control mt-2"
                      placeholder="Solvent/buffer"
                    />
                  </div>
                )}
              </Form.Group>
              <Form.Group
                controlId="endpoints.dna"
                className={`field-margin mt-2 ${
                  values.endpoints.dna
                    ? "p-3 mt-3 mb-3 border rounded bg-light-subtle"
                    : ""
                }`}
              >
                <div className="form-check">
                  <input
                    type="checkbox"
                    name="endpoints.dna"
                    checked={values.endpoints.dna}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setFieldValue("endpoints.dna", checked);
                      if (!checked) {
                        setFieldValue("endpoints.dnaAmount", "");
                        setFieldValue("endpoints.dnaVolume", "");
                        setFieldValue("endpoints.dnaBuffer", "");
                      }
                    }}
                    className="form-check-input"
                  />

                  <Form.Label className="form-check-label">
                    DNA Extraction
                  </Form.Label>
                </div>
                {values.endpoints.dna && (
                  <div className="p-3 mt-3 mb-3 border rounded bg-light-subtle">
                    <Field
                      name="endpoints.dnaAmount"
                      className="form-control mt-2"
                      placeholder="Desired amount (µg)"
                    />
                    <Field
                      name="endpoints.dnaVolume"
                      className="form-control mt-2"
                      placeholder="Final volume (µL)"
                    />
                    <Field
                      name="endpoints.dnaBuffer"
                      className="form-control mt-2"
                      placeholder="Solvent/buffer"
                    />
                  </div>
                )}
              </Form.Group>
              <Form.Group
                controlId="endpoints.rna"
                className={`field-margin mt-2 ${
                  values.endpoints.rna
                    ? "p-3 mt-3 mb-3 border rounded bg-light-subtle"
                    : ""
                }`}
              >
                <div className="form-check">
                  <input
                    type="checkbox"
                    name="endpoints.rna"
                    checked={values.endpoints.rna}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setFieldValue("endpoints.rna", checked);
                      if (!checked) {
                        setFieldValue("endpoints.rnaAmount", "");
                        setFieldValue("endpoints.rnaVolume", "");
                        setFieldValue("endpoints.rnaBuffer", "");
                      }
                    }}
                    className="form-check-input"
                  />

                  <Form.Label className="form-check-label">
                    RNA Extraction
                  </Form.Label>
                </div>
                {values.endpoints.rna && (
                  <div className="p-3 mt-3 mb-3 border rounded bg-light-subtle">
                    <Field
                      name="endpoints.rnaAmount"
                      className="form-control mt-2"
                      placeholder="Desired amount (µg)"
                    />
                    <Field
                      name="endpoints.rnaVolume"
                      className="form-control mt-2"
                      placeholder="Final volume (µL)"
                    />
                    <Field
                      name="endpoints.rnaBuffer"
                      className="form-control mt-2"
                      placeholder="Solvent/buffer"
                    />
                  </div>
                )}
              </Form.Group>
              <Form.Group
                controlId="endpoints.other"
                className={`field-margin mt-2 ${
                  values.endpoints.enableOther
                    ? "p-3 mt-3 mb-3 border rounded bg-light-subtle"
                    : ""
                }`}
              >
                <div className="form-check">
                  <input
                    type="checkbox"
                    name="endpoints.enableOther"
                    checked={values.endpoints.enableOther}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setFieldValue("endpoints.enableOther", checked);
                      if (!checked) {
                        setFieldValue("endpoints.other", "");
                      }
                    }}
                    className="form-check-input"
                  />
                  <Form.Label className="form-check-label">
                    Other (please specify)
                  </Form.Label>
                </div>

                {values.endpoints.enableOther && (
                  <div className="p-3 mt-3 mb-3">
                    <Field
                      as="textarea"
                      name="endpoints.other"
                      className="form-control"
                      placeholder="Other endpoint details"
                      rows={3}
                    />
                  </div>
                )}
              </Form.Group>

              <hr className="mt-4 mb-4 border-5 border-dark" />
              <Form.Group
                controlId="generalComments"
                className={"field-margin mt-2"}
              >
                <Form.Label>General Comments and Remarks</Form.Label>
                <Field
                  as="textarea"
                  name="generalComments"
                  className="form-control"
                  placeholder="Did not find the relevant options for your experiment? General queries or anything else we should know?
                   Let us know here..."
                  rows={4}
                />
              </Form.Group>

              {isSubmitting ? (
                <Button
                  style={{ textAlign: "center", marginTop: "2rem" }}
                  variant="primary"
                  disabled
                >
                  <Spinner
                    size="sm"
                    as="span"
                    animation="border"
                    role="status"
                    aria-hidden="true"
                  />
                </Button>
              ) : (
                <Button
                  style={{ textAlign: "center", marginTop: "2rem" }}
                  variant="primary"
                  onClick={handleSubmit}
                >
                  Submit
                </Button>
              )}
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

export default OrganoidExperimentForm;
